import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import styled from "styled-components";
import { FaTags, FaPlus, FaSearch, FaSave, FaCheckCircle, FaBan } from "react-icons/fa";
import { isMultiplePricingEnabled, priceListRequest } from "../utils/priceLists";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";

const readProfile = () => JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};

export default function PriceListsPage() {
  const navigate = useNavigate();
  const profile = useMemo(readProfile, []);
  const restaurantid = profile?.id;
  const enabled = isMultiplePricingEnabled(profile);
  const [lists, setLists] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [name, setName] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [draft, setDraft] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadLists = useCallback(async () => {
    if (!restaurantid || !enabled) return;
    setLoading(true);
    try {
      const data = await priceListRequest("list", { restaurantid, include_inactive: true });
      setLists(data?.price_lists || []);
      setSelectedId((current) => current || String(data?.price_lists?.[0]?.id || ""));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [enabled, restaurantid]);

  useEffect(() => { loadLists(); }, [loadLists]);

  useEffect(() => {
    if (!selectedId) { setCatalog([]); setDraft({}); return; }
    let active = true;
    setLoading(true);
    priceListRequest("catalog", { restaurantid, price_list_id: Number(selectedId) })
      .then((data) => {
        if (!active) return;
        const products = data?.products || [];
        const next = {};
        products.forEach((product) => {
          next[`product:${product.id}`] = product.override_price ?? "";
          (product.variants || []).forEach((item) => { next[`variant:${item.id}`] = item.override_price ?? ""; });
          (product.addons || []).forEach((item) => { next[`addon:${item.id}`] = item.override_price ?? ""; });
        });
        setCatalog(products);
        setDraft(next);
      })
      .catch((error) => active && toast.error(error.message))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [restaurantid, selectedId]);

  const createList = async () => {
    const clean = name.trim();
    if (!clean) return toast.info("Enter a price list name.");
    try {
      const data = await priceListRequest("save", { restaurantid, name: clean, is_active: 1 });
      setName("");
      await loadLists();
      setSelectedId(String(data?.price_list?.id || ""));
      toast.success("Price list created.");
    } catch (error) { toast.error(error.message); }
  };

  const toggleList = async (list) => {
    try {
      await priceListRequest("save", { restaurantid, price_list_id: list.id, name: list.name, is_active: Number(list.is_active) ? 0 : 1 });
      await loadLists();
    } catch (error) { toast.error(error.message); }
  };

  const deleteList = async (list) => {
    if (!window.confirm(`Delete “${list.name}”? Regular prices will not be changed.`)) return;
    try {
      await priceListRequest("delete", { restaurantid, price_list_id: list.id });
      setSelectedId("");
      await loadLists();
      toast.success("Price list deleted.");
    } catch (error) { toast.error(error.message); }
  };

  const savePrices = async () => {
    setSaving(true);
    try {
      const items = Object.entries(draft).map(([key, value]) => {
        const [entity_type, entity_id] = key.split(":");
        return {
          entity_type,
          entity_id: isFullOfflineModeEnabled() ? entity_id : Number(entity_id),
          price: value === "" ? null : Number(value),
        };
      });
      await priceListRequest("items/save", { restaurantid, price_list_id: Number(selectedId), items });
      toast.success("Prices saved.");
    } catch (error) { toast.error(error.message); }
    finally { setSaving(false); }
  };

  if (!enabled) return <Page><Header><div><Tag>Product pricing</Tag><h1>Price Lists</h1><p>Create custom rates without changing regular product prices.</p></div><HeaderIcon><FaTags /></HeaderIcon></Header><DisabledState><HeaderIcon><FaTags /></HeaderIcon><h2>Multiple Price Lists are off</h2><p>Enable this feature from Profile → Edit Profile first.</p><PrimaryButton onClick={() => navigate("/profile")}>Open Profile</PrimaryButton></DisabledState></Page>;

  const filteredCatalog = catalog.filter((product) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return [
      product.name,
      ...(product.variants || []).map((item) => item.name),
      ...(product.addons || []).map((item) => item.name),
    ].some((value) => String(value || "").toLowerCase().includes(query));
  });

  const priceRow = (type, item, label) => <PriceRow key={`${type}:${item.id}`}>
    <div><strong>{label || item.name}</strong><small>Regular ₹{Number(item.regular_price || 0).toFixed(2)}</small></div>
    <input type="number" min="0" step="0.01" placeholder="Use Regular" value={draft[`${type}:${item.id}`] ?? ""}
      onChange={(event) => setDraft((old) => ({ ...old, [`${type}:${item.id}`]: event.target.value }))} />
  </PriceRow>;

  return <Page>
    <ToastContainer position="top-right" autoClose={2200} />
    <Header><div><Tag>Product pricing</Tag><h1>Price Lists</h1><p>Create custom rates without changing regular product prices.</p></div><HeaderIcon><FaTags /></HeaderIcon></Header>
    <Layout>
      <Card>
        <CardTitle><div><h3>Your price lists</h3><p>{lists.length} lists configured</p></div></CardTitle>
        <CreateRow><input value={name} maxLength={80} placeholder="Internal, Wholesale, VIP…" onChange={(e) => setName(e.target.value)} onKeyDown={(e)=>e.key === "Enter" && createList()} /><button onClick={createList}><FaPlus/> Create</button></CreateRow>
        {lists.map((list) => <ListButton $active={String(list.id) === selectedId} key={list.id} onClick={() => setSelectedId(String(list.id))}>
          <span><b>{list.name}</b><small>{Number(list.is_active) ? <><FaCheckCircle/> Active</> : <><FaBan/> Inactive</>}</small></span>
          <span><button onClick={(e) => { e.stopPropagation(); toggleList(list); }}>{Number(list.is_active) ? "Disable" : "Enable"}</button><button onClick={(e) => { e.stopPropagation(); deleteList(list); }}>Delete</button></span>
        </ListButton>)}
      </Card>
      <Card>
        <CardTop><div><h3>Product prices</h3><p>Leave a field blank to continue using its regular price.</p></div><button disabled={!selectedId || saving} onClick={savePrices}><FaSave/> {saving ? "Saving…" : "Save Prices"}</button></CardTop>
        <SearchBox><FaSearch/><SearchInput
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search products, variants or add-ons…"
          aria-label="Search price list products"
        /></SearchBox>
        {loading ? <p>Loading…</p> : filteredCatalog.length ? filteredCatalog.map((product) => <Product key={product.id}>
          {priceRow("product", product, product.name)}
          {(product.variants || []).length ? <Options><b>Variants</b>{product.variants.map((item) => priceRow("variant", item))}</Options> : null}
          {(product.addons || []).length ? <Options><b>Add-ons</b>{product.addons.map((item) => priceRow("addon", item))}</Options> : null}
        </Product>) : <Empty>No matching products found.</Empty>}
      </Card>
    </Layout>
  </Page>;
}

const Page = styled.main`padding:24px;background:#f4f7fb;min-height:100vh;color:#172033;button{cursor:pointer;border:0;border-radius:9px;padding:10px 14px;background:var(--app-primary);color:white;font-weight:800}input{border:1px solid #d7e1e9;border-radius:9px;padding:11px;outline:none;&:focus{border-color:var(--app-primary);box-shadow:0 0 0 3px #e1f3fc}}@media(max-width:650px){padding:14px}`;
const Header = styled.header`margin-bottom:18px;padding:27px 30px;border-radius:22px;color:#fff;background:linear-gradient(135deg,var(--app-primary),var(--app-primary-hover));display:flex;justify-content:space-between;align-items:center;box-shadow:0 14px 30px rgba(var(--app-primary-rgb), .17);h1{margin:5px 0;font-size:2rem}p{margin:0;color:#dff3ff}@media(max-width:600px){padding:22px 18px}`;
const Tag=styled.div`text-transform:uppercase;letter-spacing:.1em;font-size:.7rem;font-weight:900;color:#bdeaff`;
const HeaderIcon=styled.div`width:58px;height:58px;flex:0 0 auto;border-radius:17px;display:grid;place-items:center;background:rgba(255,255,255,.15);font-size:1.35rem`;
const Layout = styled.div`display:grid;grid-template-columns:minmax(260px,340px) minmax(0,1fr);gap:18px;@media(max-width:850px){grid-template-columns:1fr}`;
const Card = styled.section`background:#fff;border:1px solid #e1e8f0;border-radius:18px;padding:19px;box-shadow:0 6px 18px rgba(18,38,63,.045);h2,h3{margin-top:0}`;
const CardTitle=styled.div`padding-bottom:13px;border-bottom:1px solid #e8eef4;margin-bottom:13px;h3{margin:0}p{margin:3px 0 0;color:#718096;font-size:.76rem}`;
const CreateRow = styled.div`display:flex;gap:8px;margin-bottom:14px;input{min-width:0;flex:1}`;
const ListButton = styled.div`display:flex;justify-content:space-between;align-items:center;gap:8px;padding:12px;margin-top:8px;border:1px solid ${p => p.$active ? "var(--app-primary-hover)" : "#e5e9f0"};background:${p => p.$active ? "var(--app-primary-soft)" : "white"};border-radius:11px;cursor:pointer;b{display:block}small{display:flex;align-items:center;gap:5px;color:#667085;margin-top:4px;font-size:.68rem}button{padding:6px 8px;margin-left:5px;font-size:10px}`;
const CardTop = styled.div`display:flex;justify-content:space-between;gap:15px;align-items:start;padding-bottom:13px;border-bottom:1px solid #e8eef4;h3{margin-bottom:0}p{margin:4px 0;color:#667085;font-size:.8rem}button{display:flex;align-items:center;gap:7px}`;
const Product = styled.div`border-top:1px solid #e8ebf0;padding:14px 0`;
const SearchBox=styled.div`position:relative;margin:16px 0 8px;color:#8b9bad;>svg{position:absolute;left:13px;top:50%;transform:translateY(-50%);z-index:1}`;
const SearchInput = styled.input`width:100%;box-sizing:border-box;padding-left:38px!important`;
const PriceRow = styled.div`display:grid;grid-template-columns:minmax(0,1fr) 150px;gap:12px;align-items:center;margin:7px 0;small{display:block;color:#667085;margin-top:3px}input{width:100%;box-sizing:border-box}`;
const Empty = styled.p`padding:20px;text-align:center;color:#667085`;
const Options = styled.div`margin:10px 0 0 22px;padding-left:14px;border-left:3px solid #e4eef7;b{font-size:12px;color:#667085;text-transform:uppercase}`;
const DisabledState=styled.section`min-height:340px;background:#fff;border:1px dashed #cad8e4;border-radius:18px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:25px;${HeaderIcon}{background:var(--app-primary-soft);color:var(--app-primary)}h2{margin:14px 0 5px}p{margin:0 0 18px;color:#718096}`;
const PrimaryButton=styled.button``;
